import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronUp } from "lucide-react";
import moreImage from "@/assets/more.jpg";
import diningHero from "@/assets/dining-hero.jpg";
import africanWildlife from "@/assets/african-wildlife.jpg";
import lodgeExterior from "@/assets/lodge-exterior.jpg";
import activities from "@/assets/activities.jpg";
import lodgeEntrance from "@/assets/gallery/lodge-entrance.jpeg";
import lodgeDriveway from "@/assets/gallery/lodge-driveway.jpeg";
import diningGuests from "@/assets/gallery/dining-guests.jpg";
import gardenUmbrellas from "@/assets/gallery/garden-umbrellas.jpg";
import safariVehicle from "@/assets/gallery/safari-vehicle.webp";
import maasaiMountain from "@/assets/gallery/maasai-mountain.jpg";
import diningSetup from "@/assets/gallery/dining-setup.webp";
import gardenSeating from "@/assets/gallery/garden-seating.jpg";
import groupPhoto from "@/assets/gallery/group-photo.webp";
import guestsGathering from "@/assets/gallery/guests-gathering.jpg";
import staffTeam from "@/assets/gallery/staff-team.jpg";
import balconyView from "@/assets/gallery/balcony-view.jpg";
import welcomeDrinks from "@/assets/gallery/welcome-drinks.webp";

const More = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [galleryFilter, setGalleryFilter] = useState("all");

  const faqs = [
    {
      question: "What is included in my stay at Kijani Arusha Airport Lodge?",
      answer: "Your stay includes comfortable accommodation, daily breakfast, access to our facilities, and complimentary WiFi. Additional services like safari tours, full board meals, and activities can be arranged."
    },
    {
      question: "Do you offer safari tours?",
      answer: "Yes, we offer customized safari packages to Tanzania's most famous national parks including Serengeti, Ngorongoro Crater, and Tarangire. Our experienced guides ensure unforgettable wildlife experiences."
    },
    {
      question: "What dining options are available?",
      answer: "We offer breakfast, half-board, and full-board options with diverse menus featuring local and international cuisine. Special dietary requirements can be accommodated with advance notice."
    },
    {
      question: "How far is the lodge from Arusha Airport?",
      answer: "Kijani Arusha Airport Lodge is conveniently located just 7 minutes from Arusha Airport, making it perfect for arrival and departure transfers."
    },
    {
      question: "What activities are available on-site?",
      answer: "Enjoy nature walks, bird watching, coffee farm tours, local artisan workshop/market visits, and cultural experiences. We also arrange day trips to Arusha city and surrounding attractions."
    }
  ];

  const policies = [
    {
      title: "Check-in & Check-out",
      content: "Check-in: 2:00 PM | Check-out: 11:00 AM. Early check-in and late check-out subject to availability."
    },
    {
      title: "Cancellation Policy", 
      content: "Free cancellation up to 48 hours before arrival. Cancellations within 48 hours are subject to one night's charge."
    },
    {
      title: "Payment Terms",
      content: "We accept major credit cards, bank transfers, and cash payments. Full payment required upon check-in for walk-in guests."
    },
    {
      title: "Children & Extra Beds",
      content: "Children under 5 stay free when using existing bedding. Extra beds available for $25/night. Maximum 2 extra beds per room."
    },
    {
      title: "Pet Policy",
      content: "Pets are not permitted at Kijani Arusha Airport Lodge to ensure the comfort of all guests and protect local wildlife."
    }
  ];

  const galleryImages = [
    { src: lodgeExterior, category: "rooms", title: "Luxury Room Interior" },
    { src: diningHero, category: "dining", title: "Outdoor Dining Experience" },
    { src: africanWildlife, category: "nature", title: "Wildlife Safari" },
    { src: activities, category: "activities", title: "Adventure Activities" },
    { src: moreImage, category: "guests", title: "Happy Guests" },
    { src: lodgeEntrance, category: "rooms", title: "Lodge Entrance" },
    { src: lodgeDriveway, category: "rooms", title: "Lodge Driveway at Dusk" },
    { src: diningGuests, category: "dining", title: "Guests Enjoying a Meal" },
    { src: diningSetup, category: "dining", title: "Restaurant Table Setup" },
    { src: gardenUmbrellas, category: "activities", title: "Garden Lounge Area" },
    { src: gardenSeating, category: "activities", title: "Outdoor Garden Seating" },
    { src: safariVehicle, category: "activities", title: "Safari Land Cruiser" },
    { src: maasaiMountain, category: "nature", title: "Maasai & Mount Meru View" },
    { src: groupPhoto, category: "guests", title: "Guests & Staff Group Photo" },
    { src: guestsGathering, category: "guests", title: "The Best Kind of Goodbyes" },
    { src: staffTeam, category: "guests", title: "Our Kijani Safari Lodge Team" },
    { src: balconyView, category: "rooms", title: "Balcony Garden View" },
    { src: welcomeDrinks, category: "guests", title: "Welcome Drinks on Arrival" }
  ];

  const galleryCategories = [
    { id: "all", label: "All Photos" },
    { id: "rooms", label: "Rooms" },
    { id: "dining", label: "Dining" },
    { id: "nature", label: "Nature" },
    { id: "activities", label: "Activities" },
    { id: "guests", label: "Guests" }
  ];

  const filteredImages = galleryFilter === "all" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === galleryFilter);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${moreImage}')` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4">
            More About Kijani Arusha Airport Lodge
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Everything you need to know for your perfect stay
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="faqs" className="space-y-8">
            <TabsList className="grid w-full grid-cols-3 bg-muted">
              <TabsTrigger value="faqs" className="text-base">FAQs</TabsTrigger>
              <TabsTrigger value="policies" className="text-base">Policies</TabsTrigger>
              <TabsTrigger value="gallery" className="text-base">Gallery</TabsTrigger>
            </TabsList>

            <TabsContent value="faqs" className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="font-heading text-3xl font-bold text-foreground mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-muted-foreground">
                  Find answers to common questions about your stay
                </p>
              </div>
              {faqs.map((faq, index) => (
                <Card key={index} className="shadow-soft">
                  <CardContent className="p-0">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full p-6 text-left flex justify-between items-center hover:bg-muted/50 transition-colors"
                    >
                      <h3 className="font-semibold text-foreground pr-4">{faq.question}</h3>
                      {openFaq === index ? (
                        <ChevronUp className="h-5 w-5 text-primary flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-primary flex-shrink-0" />
                      )}
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-6 pt-0">
                        <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="policies" className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="font-heading text-3xl font-bold text-foreground mb-4">
                  Lodge Policies
                </h2>
                <p className="text-muted-foreground">
                  Important information about your stay with us
                </p>
              </div>
              {policies.map((policy, index) => (
                <Card key={index} className="shadow-soft">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-3">{policy.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{policy.content}</p>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="gallery" className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="font-heading text-3xl font-bold text-foreground mb-4">
                  Our Gallery
                </h2>
                <p className="text-muted-foreground mb-8">
                  Take a peek into life at Kijani Arusha Airport Lodge
                </p>
                
                <div className="flex flex-wrap justify-center gap-2">
                  {galleryCategories.map((category) => (
                    <Button
                      key={category.id}
                      variant={galleryFilter === category.id ? "default" : "outline"}
                      onClick={() => setGalleryFilter(category.id)}
                      className={galleryFilter === category.id ? "bg-primary" : ""}
                    >
                      {category.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredImages.map((image, index) => (
                  <div key={index} className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-elegant group">
                    <img 
                      src={image.src} 
                      alt={image.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <h3 className="font-heading text-lg font-bold">{image.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default More;